import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { SessionStorageService } from 'ngx-webstorage';
import { LANGUAGES } from '@cupoui/core/language/language.constants';
import { AccountService } from '@cupoui/core/auth/account.service';
import { ProfileService } from '@cupoui/layouts/profiles/profile.service';
import { FilterRequestService } from '../../board/components/cases/travel/service/filter-request.service';
import { AccountRoleService } from '@cupoui/core/auth/account-role.service';
import { UserService } from '@cupoui/core/user/user.service';
import { CustomerProfiles, CustomerProfilesAdapter } from '@cupoui/shared/model/customerProfiles';
import { NGXLogger } from 'ngx-logger';
import { TranslateService } from '@ngx-translate/core';
import { Permission } from '@cupoui/shared/model/permission';

@Component({
    selector: 'app-sidenav',
    templateUrl: './sidenav.component.html',
    styleUrls: ['./sidenav.component.scss'],
    standalone: false
})
export class SidenavComponent implements OnInit {
  inProduction?: boolean;
  languages = LANGUAGES;
  swaggerEnabled?: boolean;
  isAdminMenuSelected = false;
  Permission = Permission;

  permissionProfiles: Map<Permission, CustomerProfiles[]> = new Map();

  private readonly permissions = [
    Permission.canAccessInsightsPageTravel,
    Permission.canViewTravelCases,
    Permission.canAccessEconomyPageTravel,
    Permission.canAccessInsightsPageMobility,
    Permission.canViewMobilityCases,
    Permission.canAccessEconomyPageMobility
  ];

  error: any;

  constructor(
    private translateService: TranslateService,
    private sessionStorage: SessionStorageService,
    private accountService: AccountService,
    private profileService: ProfileService,
    private filterRequestService: FilterRequestService,
    public accountRoleService: AccountRoleService,
    private userService: UserService,
    private customerProfilesAdapter: CustomerProfilesAdapter,
    private router: Router,
    private logger: NGXLogger
  ) {}

  ngOnInit(): void {
    this.loadProfiles();
  }

  isCasesRouteActive(): boolean {
    return this.router.url.includes('/cases');
  }

  isInsightsRouteActive(): boolean {
    return this.router.url.includes('/insights');
  }

  isEconomyRouteActive(): boolean {
    return this.router.url.includes('/economy');
  }

  isAdminMenuActive(): boolean {
    this.isAdminMenuSelected = this.router.url.includes('/admin');
    return this.router.url.includes('/admin');
  }

  changeLanguage(languageKey: string): void {
    this.sessionStorage.store('locale', languageKey);
    this.translateService.use(languageKey);
  }

  isAuthenticated(): boolean {
    return this.accountService.isAuthenticated();
  }

  toggleAdminMenu(): void {
    this.isAdminMenuSelected = !this.isAdminMenuSelected;
  }

  resetFilterRequest(): void {
    this.filterRequestService.init();
  }

  profiles(): string[] {
    return this.accountService.getCustomerProfiles();
  }

  private loadProfiles() {
    if (!this.isAuthenticated()) {
      return;
    }
    this.permissions.forEach(permission => {
      this.userService.customerProfilesByPermission(permission).subscribe(result => {
        this.permissionProfiles.set(permission, result?.customerProfiles ?? []);
      });
    });
  }

  canView(permission: Permission): boolean {
    const profiles = this.permissionProfiles.get(permission);

    return profiles?.length > 0;
  }

  // Helping methods were made since it was complicated to call the canView method directly in the HTML

  canViewMobilityCases(): boolean {
    return this.canView(Permission.canViewMobilityCases);
  }

  canAccessMobilityEconomy(): boolean {
    return this.canView(Permission.canAccessEconomyPageMobility);
  }

  canAccessMobilityInsights(): boolean {
    return this.canView(Permission.canAccessInsightsPageMobility);
  }

  canViewTravelCases(): boolean {
    return this.canView(Permission.canViewTravelCases);
  }

  canAccessTravelEconomy(): boolean {
    return this.canView(Permission.canAccessEconomyPageTravel);
  }

  canAccessTravelInsights(): boolean {
    return this.canView(Permission.canAccessInsightsPageTravel);
  }
}
